import React, { useState, useEffect, useMemo } from 'react';
import api, { assetUrl } from '../utils/api';
import toast from 'react-hot-toast';
import { Download, BookOpen, ChevronDown, ChevronRight, ExternalLink, Plus, Pencil, Trash2, FileText, ArrowUp, ArrowDown } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import Markdown from './Markdown';
import DocPageEditorModal from './DocPageEditorModal';

const METHOD_COLORS = { GET: '#30d986', POST: '#4f8ef7', PUT: '#f5a623', PATCH: '#9d6ef5', DELETE: '#f05c5c', HEAD: '#8892a4', OPTIONS: '#8892a4' };
const STATUS_COLORS = { 2: '#30d986', 3: '#4f8ef7', 4: '#f5a623', 5: '#f05c5c' };

function getStatusColor(code) {
  return STATUS_COLORS[String(code)[0]] || '#8892a4';
}

export default function DocsPanel({ projectId, project }) {
  const { user } = useAuth();
  const canEdit = user?.role !== 'client';

  const [endpoints, setEndpoints] = useState([]);
  const [folders, setFolders] = useState([]);
  const [expanded, setExpanded] = useState({});
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(false);

  // Authored guide pages ("Getting Started" etc.). A page being open is
  // tracked separately from `selected`, which holds 'overview' or an endpoint.
  const [pages, setPages] = useState([]);
  const [selectedPage, setSelectedPage] = useState(null);
  const [editing, setEditing] = useState(null); // { page } — page null means "new"

  useEffect(() => { if (projectId) { load(); loadPages(); } }, [projectId]);

  async function load() {
    setLoading(true);
    try {
      const res = await api.get(`/projects/${projectId}/endpoints`);
      setEndpoints(res.data.endpoints || []);
      setFolders(res.data.folders || []);
      if (res.data.endpoints?.length) setSelected(res.data.endpoints[0]);
    } catch {}
    finally { setLoading(false); }
  }

  async function loadPages() {
    try {
      const res = await api.get(`/projects/${projectId}/doc-pages`);
      setPages(res.data || []);
    } catch {}
  }

  async function loadDetail(ep) {
    setSelectedPage(null);
    try {
      const res = await api.get(`/endpoints/${ep.id}`);
      setSelected(res.data);
    } catch {}
  }

  function openPage(page) {
    setSelectedPage(page);
    setSelected(null);
  }

  async function deletePage(page) {
    if (!window.confirm(`Delete "${page.title}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/doc-pages/${page.id}`);
      setSelectedPage(null);
      setSelected('overview');
      loadPages();
      toast.success('Page deleted');
    } catch { toast.error('Failed to delete page'); }
  }

  // Swap a page with its neighbour and persist the whole ordering.
  async function movePage(page, dir) {
    const ordered = [...pages];
    const idx = ordered.findIndex(p => p.id === page.id);
    const swap = idx + dir;
    if (idx < 0 || swap < 0 || swap >= ordered.length) return;
    [ordered[idx], ordered[swap]] = [ordered[swap], ordered[idx]];
    setPages(ordered);
    try {
      await api.post(`/projects/${projectId}/doc-pages/reorder`, { page_ids: ordered.map(p => p.id) });
    } catch {
      toast.error('Failed to reorder');
      loadPages();
    }
  }

  function onPageSaved(saved) {
    loadPages();
    if (selectedPage?.id === saved.id) setSelectedPage(saved);
    else openPage(saved);
  }

  // Group pages into their sections, keeping the order the pages come back in.
  const sections = useMemo(() => {
    const bySection = new Map();
    pages.forEach(p => {
      const name = p.section || 'Getting Started';
      if (!bySection.has(name)) bySection.set(name, []);
      bySection.get(name).push(p);
    });
    return [...bySection.entries()].map(([name, items]) => ({ name, items }));
  }, [pages]);

  const sectionNames = useMemo(() => sections.map(s => s.name), [sections]);

  async function exportOpenAPI() {
    try {
      const res = await api.get(`/projects/${projectId}/export/openapi`);
      const blob = new Blob([JSON.stringify(res.data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = `${project?.name || 'api'}-openapi.json`; a.click();
      URL.revokeObjectURL(url);
      toast.success('OpenAPI spec downloaded!');
    } catch { toast.error('Export failed'); }
  }

  function toggle(id) { setExpanded(e => ({ ...e, [id]: !e[id] })); }

  const rootEndpoints = endpoints.filter(e => !e.folder_id);

  const STATUS_LABELS = { designing: '🔵 Designing', developing: '🟡 Developing', testing: '🔴 Testing', released: '🟢 Released', deprecated: '⚫ Deprecated' };

  return (
    <div style={sty.root}>
      <div style={sty.header}>
        <div style={sty.headerLeft}>
          {project?.logo_url && (
            <div style={sty.headerLogo}>
              <img src={assetUrl(project.logo_url)} alt={`${project.name} logo`} style={sty.logoImg} />
            </div>
          )}
          <div>
            <h2 style={sty.title}>API Documentation</h2>
            <p style={sty.sub}>{endpoints.length} endpoint{endpoints.length !== 1 ? 's' : ''} documented</p>
          </div>
        </div>
        <button className="btn btn-ghost btn-sm" onClick={exportOpenAPI}>
          <Download size={13} /> Export OpenAPI 3.0
        </button>
      </div>

      <div style={sty.body}>
        {/* Nav */}
        <div style={sty.nav}>
          <div style={sty.navSection}>
            <div style={sty.navSectionTitle}>Overview</div>
            <button style={{ ...sty.navItem, ...(selected === 'overview' ? sty.navItemActive : {}) }}
              onClick={() => { setSelected('overview'); setSelectedPage(null); }}>
              <BookOpen size={13} /> Overview
            </button>
          </div>

          {/* Authored guide sections — "Getting Started" and friends */}
          {sections.map(section => (
            <div key={section.name} style={sty.navSection}>
              <div style={sty.navSectionTitle}>{section.name}</div>
              {section.items.map(page => (
                <button key={page.id}
                  style={{ ...sty.navItem, ...(selectedPage?.id === page.id ? sty.navItemActive : {}) }}
                  onClick={() => openPage(page)}>
                  <FileText size={13} />
                  <span style={sty.epNavName}>{page.title}</span>
                  {!page.is_published && <span style={sty.draftDot} title="Draft — hidden from published docs" />}
                </button>
              ))}
            </div>
          ))}

          {canEdit && (
            <div style={sty.navSection}>
              <button style={sty.addPageBtn} onClick={() => setEditing({ page: null })}>
                <Plus size={12} /> New page
              </button>
            </div>
          )}

          <div style={sty.navSection}>
            <div style={sty.navSectionTitle}>Endpoints</div>
            {/* Folders */}
            {folders.map(folder => {
              const fEps = endpoints.filter(e => e.folder_id === folder.id);
              return (
                <div key={folder.id}>
                  <button style={sty.folderBtn} onClick={() => toggle(folder.id)}>
                    {expanded[folder.id] ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
                    {folder.name}
                    <span style={sty.epBadge}>{fEps.length}</span>
                  </button>
                  {expanded[folder.id] && fEps.map(ep => (
                    <button key={ep.id} style={{ ...sty.navEpItem, ...(selected?.id === ep.id ? sty.navEpItemActive : {}), paddingLeft: 24 }}
                      onClick={() => loadDetail(ep)}>
                      <span style={{ ...sty.methodDot, color: METHOD_COLORS[ep.method] }}>{ep.method}</span>
                      <span style={sty.epNavName}>{ep.name}</span>
                    </button>
                  ))}
                </div>
              );
            })}
            {/* Root */}
            {rootEndpoints.map(ep => (
              <button key={ep.id} style={{ ...sty.navEpItem, ...(selected?.id === ep.id ? sty.navEpItemActive : {}) }}
                onClick={() => loadDetail(ep)}>
                <span style={{ ...sty.methodDot, color: METHOD_COLORS[ep.method] }}>{ep.method}</span>
                <span style={sty.epNavName}>{ep.name}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Content */}
        <div style={sty.content}>
          {selectedPage && (
            <div style={sty.docContent}>
              <div style={sty.pageHead}>
                <div style={{ minWidth: 0 }}>
                  <div style={sty.pageSection}>{selectedPage.section}</div>
                  <h1 style={sty.pageTitle}>{selectedPage.title}</h1>
                </div>
                {canEdit && (
                  <div style={sty.pageActions}>
                    <button className="btn btn-ghost btn-sm" title="Move up" onClick={() => movePage(selectedPage, -1)}><ArrowUp size={13} /></button>
                    <button className="btn btn-ghost btn-sm" title="Move down" onClick={() => movePage(selectedPage, 1)}><ArrowDown size={13} /></button>
                    <button className="btn btn-ghost btn-sm" onClick={() => setEditing({ page: selectedPage })}><Pencil size={13} /> Edit</button>
                    <button className="btn btn-ghost btn-sm" onClick={() => deletePage(selectedPage)}><Trash2 size={13} /></button>
                  </div>
                )}
              </div>
              {!selectedPage.is_published && (
                <div style={sty.draftBanner}>This page is a draft — it stays hidden from published documentation.</div>
              )}
              {selectedPage.content?.trim()
                ? <Markdown content={selectedPage.content} />
                : <p style={{ color: 'var(--text-muted)', fontSize: 13 }}>This page has no content yet.</p>}
            </div>
          )}

          {!selectedPage && selected === 'overview' && (
            <div style={sty.overviewSection}>
              {project?.logo_url && (
                <div style={sty.overviewLogo}>
                  <img src={assetUrl(project.logo_url)} alt={`${project.name} logo`} style={sty.logoImg} />
                </div>
              )}
              <h1 style={sty.overviewTitle}>{project?.name}</h1>
              {project?.description && <p style={sty.overviewDesc}>{project.description}</p>}
              {project?.base_url && (
                <div style={sty.baseUrlBlock}>
                  <div style={sty.blockLabel}>Base URL</div>
                  <code style={sty.baseUrlCode}>{project.base_url}</code>
                </div>
              )}
              <div style={sty.statsRow}>
                <div style={sty.stat}><span style={sty.statNum}>{endpoints.length}</span><span style={sty.statLabel}>Endpoints</span></div>
                <div style={sty.stat}><span style={sty.statNum}>{folders.length}</span><span style={sty.statLabel}>Folders</span></div>
              </div>
            </div>
          )}

          {!selectedPage && selected && selected !== 'overview' && (
            <div style={sty.docContent}>
              {/* Title */}
              <div style={sty.epHeader}>
                <div style={sty.epTitleRow}>
                  <span style={{ ...sty.methodBig, background: `${METHOD_COLORS[selected.method]}22`, color: METHOD_COLORS[selected.method] }}>
                    {selected.method}
                  </span>
                  <h1 style={sty.epTitle}>{selected.name}</h1>
                  {selected.status && <span style={sty.statusTag}>{STATUS_LABELS[selected.status] || selected.status}</span>}
                </div>
                <code style={sty.epPath}>{selected.path}</code>
                {selected.description && <p style={sty.epDesc}>{selected.description}</p>}
              </div>

              {/* Parameters */}
              {selected.params?.length > 0 && (
                <DocSection title="Parameters">
                  <table className="table">
                    <thead>
                      <tr>
                        <th>Name</th>
                        <th>In</th>
                        <th>Type</th>
                        <th>Required</th>
                        <th>Description</th>
                        <th>Example</th>
                      </tr>
                    </thead>
                    <tbody>
                      {selected.params.map((p, i) => (
                        <tr key={i}>
                          <td><code style={sty.paramName}>{p.name}</code></td>
                          <td><span style={sty.paramIn}>{p.param_in}</span></td>
                          <td><span style={sty.paramType}>{p.type}</span></td>
                          <td>{p.required ? <span style={sty.required}>required</span> : <span style={sty.optional}>optional</span>}</td>
                          <td style={{ color: 'var(--text-secondary)' }}>{p.description || '—'}</td>
                          <td><code style={sty.example}>{p.example_value || '—'}</code></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </DocSection>
              )}

              {/* Request body */}
              {selected.body && (
                <DocSection title="Request Body">
                  <div style={sty.contentTypeBadge}>{selected.body.content_type}</div>
                  {selected.body.example_json && (
                    <>
                      <div style={sty.exampleLabel}>Example</div>
                      <pre style={sty.codeBlock} className="mono">
                        {(() => { try { return JSON.stringify(JSON.parse(selected.body.example_json), null, 2); } catch { return selected.body.example_json; } })()}
                      </pre>
                    </>
                  )}
                </DocSection>
              )}

              {/* Responses */}
              {selected.responses?.length > 0 && (
                <DocSection title="Responses">
                  {selected.responses.map((r, i) => (
                    <div key={i} style={sty.responseDoc}>
                      <div style={sty.responseDocHeader}>
                        <span style={{ ...sty.statusCode, color: getStatusColor(r.status_code) }}>{r.status_code}</span>
                        <span style={sty.responseName}>{r.name}</span>
                        <span style={sty.responseContentType}>{r.content_type}</span>
                      </div>
                      {r.example_json && (
                        <pre style={sty.codeBlock} className="mono">
                          {(() => { try { return JSON.stringify(JSON.parse(r.example_json), null, 2); } catch { return r.example_json; } })()}
                        </pre>
                      )}
                    </div>
                  ))}
                </DocSection>
              )}
            </div>
          )}

          {!selected && !selectedPage && !loading && (
            <div style={sty.noSel}>
              <BookOpen size={40} style={{ opacity: 0.1 }} />
              <p style={{ color: 'var(--text-muted)', marginTop: 12 }}>Select an endpoint to view documentation</p>
            </div>
          )}
        </div>
      </div>

      {editing && (
        <DocPageEditorModal
          projectId={projectId}
          page={editing.page}
          sections={sectionNames}
          onClose={() => setEditing(null)}
          onSaved={onPageSaved}
        />
      )}
    </div>
  );
}

function DocSection({ title, children }) {
  return (
    <div style={{ marginBottom: 28 }}>
      <h3 style={{ fontSize: 14, fontWeight: 700, color: 'var(--text-secondary)', marginBottom: 12, paddingBottom: 8, borderBottom: '1px solid var(--border)' }}>{title}</h3>
      {children}
    </div>
  );
}

const sty = {
  root: { display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' },
  header: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 24px', borderBottom: '1px solid var(--border)', background: 'var(--bg-secondary)' },
  headerLeft: { display: 'flex', alignItems: 'center', gap: 12, minWidth: 0 },
  title: { fontSize: 18, fontWeight: 700, marginBottom: 2 },
  sub: { fontSize: 12, color: 'var(--text-muted)' },
  // Uploaded logos are usually dark-inked artwork on transparency, so they
  // need a light plate to stay legible against this dark chrome.
  headerLogo: {
    height: 38, minWidth: 38, borderRadius: 8, padding: '0 8px', flexShrink: 0, display: 'flex',
    alignItems: 'center', justifyContent: 'center', background: '#fff', border: '1px solid var(--border)',
  },
  overviewLogo: {
    height: 56, minWidth: 56, maxWidth: 220, borderRadius: 10, padding: '0 12px', marginBottom: 16,
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center', background: '#fff',
    border: '1px solid var(--border)',
  },
  logoImg: { maxHeight: '100%', maxWidth: '100%', objectFit: 'contain', display: 'block' },
  body: { display: 'flex', flex: 1, overflow: 'hidden' },
  nav: { width: 220, borderRight: '1px solid var(--border)', overflow: 'auto', padding: '12px 8px' },
  navSection: { marginBottom: 16 },
  navSectionTitle: { fontSize: 10, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.8px', padding: '4px 10px', marginBottom: 2 },
  navItem: { display: 'flex', alignItems: 'center', gap: 7, width: '100%', padding: '7px 10px', borderRadius: 6, background: 'none', color: 'var(--text-secondary)', fontSize: 12, cursor: 'pointer', fontFamily: 'var(--sans)', textAlign: 'left' },
  navItemActive: { background: 'var(--accent-dim)', color: 'var(--accent)' },
  draftDot: { width: 6, height: 6, borderRadius: '50%', background: 'var(--orange)', flexShrink: 0, marginLeft: 'auto' },
  addPageBtn: {
    display: 'flex', alignItems: 'center', gap: 6, width: '100%', padding: '7px 10px', borderRadius: 6,
    background: 'none', border: '1px dashed var(--border-light)', color: 'var(--text-muted)',
    fontSize: 11.5, cursor: 'pointer', fontFamily: 'var(--sans)',
  },
  pageHead: { display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 16, marginBottom: 20 },
  pageSection: { fontSize: 10, fontWeight: 700, color: 'var(--accent)', textTransform: 'uppercase', letterSpacing: '0.8px', marginBottom: 6 },
  pageTitle: { fontSize: 28, fontWeight: 800, margin: 0, lineHeight: 1.25 },
  pageActions: { display: 'flex', gap: 6, flexShrink: 0 },
  draftBanner: {
    background: 'rgba(251,146,60,0.08)', border: '1px solid rgba(251,146,60,0.25)', color: 'var(--orange)',
    borderRadius: 8, padding: '9px 12px', fontSize: 12, marginBottom: 18,
  },
  folderBtn: { display: 'flex', alignItems: 'center', gap: 6, width: '100%', padding: '6px 10px', borderRadius: 6, background: 'none', color: 'var(--text-secondary)', fontSize: 12, cursor: 'pointer', fontFamily: 'var(--sans)', textAlign: 'left' },
  epBadge: { fontSize: 10, background: 'var(--bg-tertiary)', padding: '1px 5px', borderRadius: 10, color: 'var(--text-muted)', marginLeft: 'auto' },
  navEpItem: { display: 'flex', alignItems: 'center', gap: 7, width: '100%', padding: '6px 10px', borderRadius: 6, background: 'none', cursor: 'pointer', fontFamily: 'var(--sans)', textAlign: 'left', paddingLeft: 12 },
  navEpItemActive: { background: 'var(--accent-dim)' },
  methodDot: { fontFamily: 'var(--mono)', fontWeight: 700, fontSize: 9, minWidth: 44, flexShrink: 0 },
  epNavName: { fontSize: 12, color: 'var(--text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  content: { flex: 1, overflow: 'auto' },
  overviewSection: { padding: 32 },
  overviewTitle: { fontSize: 28, fontWeight: 800, marginBottom: 10 },
  overviewDesc: { color: 'var(--text-secondary)', marginBottom: 20, lineHeight: 1.7, maxWidth: 600 },
  baseUrlBlock: { background: 'var(--bg-secondary)', border: '1px solid var(--border)', borderRadius: 8, padding: '14px 18px', marginBottom: 20, display: 'inline-block' },
  blockLabel: { fontSize: 10, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 6 },
  baseUrlCode: { fontFamily: 'var(--mono)', color: 'var(--accent)', fontSize: 14 },
  statsRow: { display: 'flex', gap: 16 },
  stat: { background: 'var(--bg-secondary)', border: '1px solid var(--border)', borderRadius: 8, padding: '14px 24px', textAlign: 'center' },
  statNum: { display: 'block', fontSize: 24, fontWeight: 700, color: 'var(--text-primary)' },
  statLabel: { fontSize: 11, color: 'var(--text-muted)', marginTop: 4 },
  docContent: { padding: '28px 36px', maxWidth: 860 },
  epHeader: { marginBottom: 28, paddingBottom: 20, borderBottom: '1px solid var(--border)' },
  epTitleRow: { display: 'flex', alignItems: 'center', gap: 12, marginBottom: 10, flexWrap: 'wrap' },
  methodBig: { padding: '4px 12px', borderRadius: 6, fontFamily: 'var(--mono)', fontWeight: 800, fontSize: 13 },
  epTitle: { fontSize: 22, fontWeight: 800 },
  statusTag: { fontSize: 11, color: 'var(--text-secondary)', background: 'var(--bg-tertiary)', padding: '3px 8px', borderRadius: 4 },
  epPath: { fontFamily: 'var(--mono)', fontSize: 14, color: 'var(--text-secondary)', display: 'block', marginBottom: 12 },
  epDesc: { color: 'var(--text-secondary)', lineHeight: 1.7 },
  paramName: { fontFamily: 'var(--mono)', color: 'var(--accent)', fontSize: 12 },
  paramIn: { fontSize: 11, background: 'var(--bg-tertiary)', padding: '2px 6px', borderRadius: 4, color: 'var(--text-secondary)' },
  paramType: { fontSize: 11, color: 'var(--accent-purple)', fontFamily: 'var(--mono)' },
  required: { fontSize: 11, color: 'var(--accent-red)', background: 'rgba(240,92,92,0.1)', padding: '1px 6px', borderRadius: 4 },
  optional: { fontSize: 11, color: 'var(--text-muted)' },
  example: { fontFamily: 'var(--mono)', fontSize: 11, color: 'var(--text-secondary)' },
  contentTypeBadge: { fontSize: 11, background: 'var(--bg-tertiary)', border: '1px solid var(--border)', padding: '3px 8px', borderRadius: 4, color: 'var(--text-secondary)', display: 'inline-block', marginBottom: 10, fontFamily: 'var(--mono)' },
  exampleLabel: { fontSize: 11, fontWeight: 600, color: 'var(--text-muted)', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.4px' },
  codeBlock: { background: 'var(--bg-primary)', border: '1px solid var(--border)', borderRadius: 8, padding: 16, fontSize: 12, color: '#a8d8a8', overflow: 'auto', maxHeight: 320, whiteSpace: 'pre-wrap', lineHeight: 1.6 },
  responseDoc: { background: 'var(--bg-primary)', border: '1px solid var(--border)', borderRadius: 8, padding: '12px 14px', marginBottom: 10 },
  responseDocHeader: { display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 },
  statusCode: { fontFamily: 'var(--mono)', fontWeight: 800, fontSize: 16 },
  responseName: { fontWeight: 600, flex: 1 },
  responseContentType: { fontSize: 11, color: 'var(--text-muted)', fontFamily: 'var(--mono)' },
  noSel: { flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: 40 },
};
